import { Migration } from '@gleamer/types';
import React, { useEffect, useState } from 'react';
import { useMigrationSegments } from '../../../../hooks/useMigration';
import { PageButton } from '../../../atoms/PageButton';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';
import { CommandButton, formatCount, SegmentStateBadge, selectClassName } from './parts';

type MigrationSegmentState = Migration.MigrationSegmentState;

const { MIGRATION_SEGMENT_STATES } = Migration;

export const MIGRATION_SEGMENTS_PAGE_SIZE = 50;

/** A run's segments — failed first is what an operator looks for, so that is the default filter. */
export function MigrationSegmentsSection({
  runId,
  groups,
  canReplay,
  canRetry,
}: {
  runId: number;
  groups: string[];
  canReplay: boolean;
  canRetry: boolean;
}) {
  const [group, setGroup] = useState<string>('');
  const [state, setState] = useState<MigrationSegmentState | ''>('FAILED');
  const [page, setPage] = useState(0);
  const { data, status, error } = useMigrationSegments(String(runId), {
    page,
    size: MIGRATION_SEGMENTS_PAGE_SIZE,
    group: group || undefined,
    states: state ? [state] : undefined,
  });

  // a retry or a replay can empty the page shown (the default filters are what they clear): go back to the last
  // page that has rows, or the pager would be gone with it
  useEffect(() => {
    if (status === 'success' && data.items.length === 0 && page > 0) {
      setPage(Math.max(0, Math.min(page - 1, data.pages - 1)));
    }
  }, [status, data, page]);

  return (
    <section
      aria-labelledby="segments-title"
      className="mb-4"
    >
      <div className="mb-2 flex flex-wrap items-end gap-4">
        <h3
          id="segments-title"
          className="mr-auto text-xl font-bold"
        >
          Segments
        </h3>
        <label className="flex flex-col text-sm">
          Group
          <select
            className={selectClassName}
            value={group}
            onChange={e => {
              setGroup(e.target.value);
              setPage(0);
            }}
          >
            <option value="">All</option>
            {groups.map(g => (
              <option
                key={g}
                value={g}
              >
                {g}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-sm">
          State
          <select
            className={selectClassName}
            value={state}
            onChange={e => {
              setState(e.target.value as MigrationSegmentState | '');
              setPage(0);
            }}
          >
            <option value="">All</option>
            {MIGRATION_SEGMENT_STATES.map(s => (
              <option
                key={s}
                value={s}
              >
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      {status === 'loading' && <div>Loading…</div>}
      {status === 'error' && (
        <div
          role="alert"
          className="text-dl-danger"
        >
          Could not load the segments: {(error as Error)?.message}
        </div>
      )}
      {status === 'success' && data.items.length === 0 && <div>No segment.</div>}
      {status === 'success' && data.items.length > 0 && (
        <>
          <table className="w-full table-auto">
            <thead className="bg-dl-track">
              <tr>
                <TableHeadTh>Segment</TableHeadTh>
                <TableHeadTh>Group</TableHeadTh>
                <TableHeadTh>Range</TableHeadTh>
                <TableHeadTh>State</TableHeadTh>
                <TableHeadTh>Kinds (read / written / skipped)</TableHeadTh>
                <TableHeadTh />
              </tr>
            </thead>
            <tbody>
              {data.items.map(segment => (
                <tr
                  key={segment.id}
                  className="group"
                >
                  <TableBodyTd>{segment.id}</TableBodyTd>
                  <TableBodyTd>{segment.group}</TableBodyTd>
                  <TableBodyTd className="break-all font-mono text-sm">
                    {segment.rangeStart ?? '(start)'} → {segment.rangeEnd ?? '(end)'}
                  </TableBodyTd>
                  <TableBodyTd>
                    <SegmentStateBadge state={segment.state} />
                    {segment.claimedBy && (
                      <div className="text-sm text-dl-muted">
                        {segment.claimedBy}, {formatDateTime(segment.heartbeatAt)}
                      </div>
                    )}
                    {segment.error && <div className="text-sm text-dl-danger">{segment.error}</div>}
                  </TableBodyTd>
                  <TableBodyTd>
                    <ul className="text-sm">
                      {segment.cursors.map(cursor => (
                        <li key={cursor.kind}>
                          {cursor.done ? '✓' : '…'} {cursor.kind}: {formatCount(cursor.read)} /{' '}
                          {formatCount(cursor.written)} / {formatCount(cursor.skipped)}
                        </li>
                      ))}
                    </ul>
                  </TableBodyTd>
                  <TableBodyTd>
                    {canRetry && segment.state === 'FAILED' && (
                      <CommandButton
                        label="Retry"
                        command={{ type: 'RETRY', runId, segmentId: segment.id }}
                      />
                    )}
                    {canReplay && segment.state !== 'RUNNING' && (
                      <CommandButton
                        label="Replay"
                        confirm="Read this segment again from its first key?"
                        command={{ type: 'REPLAY', runId, segmentId: segment.id }}
                      />
                    )}
                  </TableBodyTd>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center py-2">
            <span className="mr-2">
              Page {data.page + 1} of {Math.max(1, data.pages)} ({data.total} segments)
            </span>
            <PageButton
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Previous page
            </PageButton>
            <PageButton
              onClick={() => setPage(p => p + 1)}
              disabled={page + 1 >= data.pages}
            >
              Next page
            </PageButton>
          </div>
        </>
      )}
    </section>
  );
}
